import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

/**
 * Admin hooks for delivery schedules (admin-delivery-schedule function).
 * The schedule jsonb travels as `unknown` — parse with parseDeliverySchedule
 * from '@/lib/deliverySchedule' where needed.
 */

export interface DeliveryScheduleResponse {
  ok: true;
  customer: { id: string; name: string; delivery_schedule: unknown; order_reminders: unknown };
  locations: Array<{
    id: string;
    display_name: string | null;
    location_code: string | null;
    active: boolean;
    delivery_schedule: unknown;
    order_reminders: unknown;
  }>;
  /** Chain master: every enabled store, each with its OWN settings. */
  stores: Array<{
    id: string;
    name: string;
    store_code: string | null;
    active: boolean;
    delivery_schedule: unknown;
    order_reminders: unknown;
  }>;
  /** Chain store: the master it belongs to. */
  member_of: { id: string; name: string } | null;
}

/** The editable shape POSTed to the server (day NAMES, not indices). */
export interface ScheduleDraft {
  enabled: boolean;
  days: string[];
  every_n_weeks: 1 | 2;
  anchor_date: string | null;
  cutoff_time: string;
}

/** The order-reminders shape POSTed to the server. */
export interface RemindersDraft {
  enabled: boolean;
  mode: 'before_delivery' | 'weekly';
  days: string[];
  time: string;
  time_zone: string;
  emails: string[];
}

export function useDeliverySchedules(customerId: string) {
  return useQuery({
    queryKey: ['admin', 'delivery-schedule', customerId],
    queryFn: () =>
      adminFetch<DeliveryScheduleResponse>(
        `admin-delivery-schedule?customer_id=${encodeURIComponent(customerId)}`,
      ),
    staleTime: 30_000,
  });
}

/**
 * Save delivery days OR order reminders. `customerId` is the page's account
 * (whose data refreshes); `target_customer_id` lets a chain master's page
 * save one of its stores, which is its own account.
 */
export function useSaveDeliverySchedule(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      input: { target_customer_id?: string; location_id?: string | null } & (
        | { schedule: ScheduleDraft | null }
        | { reminders: RemindersDraft | null }
      ),
    ) => {
      const { target_customer_id, ...rest } = input;
      return adminFetch<{ ok: true }>('admin-delivery-schedule', {
        method: 'POST',
        body: JSON.stringify({ customer_id: target_customer_id ?? customerId, ...rest }),
      });
    },
    onSuccess: (_d, input) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'delivery-schedule', customerId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'reminder-preview', customerId] });
      if (input.target_customer_id) {
        void qc.invalidateQueries({ queryKey: ['admin', 'delivery-schedule', input.target_customer_id] });
      }
      // The detail loader also reads customers/customer_locations directly.
      void qc.invalidateQueries({ queryKey: ['admin', 'customer-detail', customerId] });
    },
  });
}

/* -------------------- reminder preview -------------------- */

export interface ReminderPreviewRow {
  location_id: string;
  location_name: string;
  location_code: string | null;
  schedule: string | null;
  cutoff: string | null;
  /** "Mondays · 2:00 PM ET" / "Day before each delivery · 9:00 AM PT" / "Off". */
  reminders: string;
  would_send: boolean;
  reason: string | null;
  kind?: 'before_delivery' | 'weekly';
  send_date?: string;
  send_time?: string;
  delivery_date?: string | null;
  recipients?: string[];
  recipient_source?: 'schedule' | 'edi_notify_list' | 'primary_email' | 'none';
  subject?: string;
  html?: string;
}

export interface ReminderPreviewResponse {
  ok: true;
  customer: { id: string; name: string };
  rows: ReminderPreviewRow[];
}

/**
 * See the cutoff reminder before a customer does. Read-only — previewing
 * never claims a slot, so it can't stop a real reminder going out.
 * `enabled: false` by default: this is opened on demand, not on page load.
 */
export function useReminderPreview(customerId: string, enabled: boolean) {
  return useQuery<ReminderPreviewResponse>({
    queryKey: ['admin', 'reminder-preview', customerId],
    queryFn: () => adminFetch(`admin-reminder-preview?customer_id=${customerId}`),
    enabled: enabled && !!customerId,
    staleTime: 30_000,
  });
}
